import type { CSSProperties, ReactNode } from 'react';

import { cx } from '@/utils/cx';

import type { ChatColumnWidth } from './chatColumnWidth';
import { toCssWidth } from './chatColumnWidth';

const WIDTH_TRANSITION = 'width 300ms ease-out';

interface ChatMainColumnProps {
  width: ChatColumnWidth;
  /** On mobile the open side panel is full screen, so the chat hides. */
  isSidePanelOpen: boolean;
  /** Any pane is being drag-resized: width must track the pointer, no easing. */
  isResizing: boolean;
  children: ReactNode;
}

/**
 * The chat column of the conversation layout. Owns its width, entrance push,
 * and resize transition — dynamic values, so this is the one place the
 * column's inline style lives.
 */
export function ChatMainColumn({
  width,
  isSidePanelOpen,
  isResizing,
  children,
}: ChatMainColumnProps) {
  // Applied straight to a DOM node, so no reference-stability concern.
  const style: CSSProperties = {
    width: toCssWidth(width),
    transition: isResizing ? 'none' : WIDTH_TRANSITION,
  };

  return (
    <div
      className={cx(
        // `min-w-0` lets this flex item shrink below its min-content width so
        // the resolved width is honored and wide chat content (code blocks,
        // tables) can't push a sibling pane off-screen — matches BuilderSplit.
        'flex-col bg-bg-primary min-w-0',
        isSidePanelOpen ? 'hidden lg:flex' : 'flex w-full'
      )}
      style={style}
    >
      {children}
    </div>
  );
}
